import React, { FC, useRef, useState, useEffect, useCallback } from 'react';
import styled from '@emotion/styled';
import { useTheme } from '@emotion/react';
import {
  Box,
  Flex,
  Image,
  Text,
  Palette,
  SpinnerFullPage,
} from '@updater/ui-uds';
import { useUserData } from 'flows/core/hooks/use-profile';

import { NativeWebElement } from '@updater/native-web';
import { BrandingProBar } from 'components/branding-pro-bar';

import { TabLayoutCardLayout, TabLayoutContainer } from 'flows/core/components';

const WhiteLabelConstraint = styled.div`
  ${({ theme }) =>
    theme.whiteLabelTheme &&
    `
    max-width: 720px;
    margin-left: auto;
    margin-right: auto;
  `}
`;
import { ProgressBarCard } from 'flows/core/components/cards/progress-bar-card/ProgressBarCard';
import { InitiatePayload } from 'flows/core/types/cards';
import { isTaskCompletedOrValidatingOrPending } from 'flows/core/utils/card-makers/utils';

import { AppBanner } from 'flows/core/components/app-banner';
import useSWR from 'swr';
import { addDays } from 'date-fns';
import { useTracking } from 'react-tracking';
import { TrackEventInput } from '@updater/ui-tracker';
import { ItemDefinition } from 'types';
import { useIsHomeOwner } from '../../../../../hooks/useIsHomeOwner';
import { useIsMoveUPO } from 'hooks/useIsMoveUPO';
import { useFeature } from '@growthbook/growthbook-react';
import { useIsPropertyValid } from 'hooks/use-is-property-valid';
import { StyledHeaderLink } from 'components/styled-menu-link';
import { Buildings } from '@phosphor-icons/react';
import { useEssentialsTabCards } from 'flows/core/hooks/use-tab-cards';
import { useDaisyChain } from 'flows/core/hooks/use-daisy-chain';
import { useRouter } from 'next/router';
import { WhiteLabelHero } from './WhiteLabelHero';

const useParallax = (heroRef: React.RefObject<HTMLElement | null>) => {
  const [opacity, setOpacity] = useState(1);
  const [translateY, setTranslateY] = useState(172); // desktop initial offset
  const [mobileTranslateY, setMobileTranslateY] = useState(140); // mobile initial offset

  const updateParallax = useCallback(() => {
    if (!heroRef.current) return;

    const heroTop = heroRef.current.getBoundingClientRect().top;
    const headerHeight = 56;
    const scrollIntoHero = Math.max(0, headerHeight - heroTop);

    // Fade content
    const fadeDistance = 200;
    const fadeProgress = Math.min(scrollIntoHero / fadeDistance, 1);
    setOpacity(1 - fadeProgress);

    // Parallax illustration
    const parallaxSpeed = 0.8;
    const desktopOffset = Math.max(172 - scrollIntoHero * parallaxSpeed, 0);
    const mobileOffset = Math.max(140 - scrollIntoHero * parallaxSpeed, 0);
    setTranslateY(desktopOffset);
    setMobileTranslateY(mobileOffset);
  }, [heroRef]);

  useEffect(() => {
    window.addEventListener('scroll', updateParallax, { passive: true });
    updateParallax();
    return () => window.removeEventListener('scroll', updateParallax);
  }, [updateParallax]);

  return { opacity, translateY, mobileTranslateY };
};

interface EssentialLayoutProps {
  loading?: boolean;
  greeting?: string | null;
  brandLogo?: string | undefined;
  propertyName?: string | null | undefined;
}

const PropertyContactPageLink = () => {
  const { value: enabled } = useFeature('consumer_header_property_contact');

  if (enabled) {
    return (
      <StyledHeaderLink
        href="/property-contact"
        data-testid="property-contact-link"
        data-cy="property-contact-link"
      >
        <Flex alignContent="center" flexDirection="row" pb="l">
          <Flex mr="xs" color="cta">
            <Buildings size={24} />
          </Flex>
          Your community info
        </Flex>
      </StyledHeaderLink>
    );
  }

  return null;
};

const PartnerImage = styled(Image)`
  mix-blend-mode: multiply;
`;

/* Hero Section - Mint green background */
const HeroSection = styled(Box)`
  background: #e5f0eb;
  overflow: hidden;
  position: relative;
  border-radius: 0 0 24px 24px;
  ${({ theme }) => theme.mediaQueries.m} {
    border-radius: 24px;
  }
`;

/* Hero inner container - handles layout */
const HeroInner = styled(Box)`
  position: relative;
  padding: 24px 20px 0;

  ${({ theme }) => theme.mediaQueries.m} {
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 40px;
    min-height: 380px;
  }
`;

/* Content that fades on scroll */
const HeroContent = styled(Box)<{ contentOpacity: number }>`
  opacity: ${({ contentOpacity }) => contentOpacity};
  will-change: opacity;
  position: relative;
  z-index: 2;

  ${({ theme }) => theme.mediaQueries.m} {
    max-width: 55%;
  }
`;

/* Mobile illustration container */
const IllustrationMobile = styled(Box)`
  display: flex;
  justify-content: center;
  height: 220px;
  overflow: hidden;
  position: relative;

  ${({ theme }) => theme.mediaQueries.m} {
    display: none;
  }
`;

/* Desktop illustration container */
const IllustrationDesktop = styled(Box)`
  display: none;

  ${({ theme }) => theme.mediaQueries.m} {
    display: block;
    position: absolute;
    right: 40px;
    top: 0;
    bottom: 0;
    width: 320px;
    overflow: hidden;
    pointer-events: none;
  }

  ${({ theme }) => theme.mediaQueries.l} {
    right: 60px;
    width: 360px;
  }
`;

/* Mobile illustration mover - handles parallax transform */
const IllustrationMoverMobile = styled(Box)<{ mobileTranslateY: number }>`
  width: 250px;
  position: absolute;
  bottom: 0;
  transform: translateY(${({ mobileTranslateY }) => mobileTranslateY}px);
  will-change: transform;
`;

/* Desktop illustration mover - handles parallax transform */
const IllustrationMoverDesktop = styled(Box)<{ desktopTranslateY: number }>`
  position: absolute;
  bottom: 0;
  right: 0;
  width: 100%;
  transform: translateY(${({ desktopTranslateY }) => desktopTranslateY}px);
  will-change: transform;
`;

/* Progress bar container for mobile (outside of fading content) */
const MobileProgressBarContainer = styled(Box)`
  margin-bottom: 20px;

  ${({ theme }) => theme.mediaQueries.m} {
    display: none;
  }
`;

/* Progress bar container for desktop */
const DesktopProgressBarContainer = styled(Box)`
  display: none;

  ${({ theme }) => theme.mediaQueries.m} {
    display: block;
    max-width: 500px;
  }
`;

const UpdaterProLanguage = {
  title: 'Experience Updater Pro with an invite',
  body: "Updater only gets better when a property partners with us to create a bespoke experience that is tailored to their client's moving needs.",
};

const useTitle = () => {
  const { user, loading } = useUserData();
  const title = user && `Let's get you ready to move.`;

  return {
    loading,
    title,
  };
};

const EssentialLayout: FC<EssentialLayoutProps> = ({
  loading = false,
  greeting,
  brandLogo,
  propertyName,
}) => {
  // White label check
  const theme = useTheme();
  const isWhiteLabeled = !!theme.whiteLabelTheme;
  // Parallax
  const heroRef = useRef<HTMLElement>(null);
  const { opacity, translateY, mobileTranslateY } = useParallax(heroRef);

  // Data hooks
  const {
    priorityCards = [],
    recommendedCards = [],
    completedCards = [],
    validatingCards = [],
    loading: cardsLoading,
  } = useEssentialsTabCards();

  const hasCompletedCards = completedCards && completedCards.length > 0;
  const completedRequiredCards = (completedCards as InitiatePayload[]).filter(
    (card) =>
      isTaskCompletedOrValidatingOrPending(card.data) &&
      (card.data?.required || card.data?.priority)
  );
  const { title, loading: loadingTitle } = useTitle();
  const isHomeowner = useIsHomeOwner();
  const { on: consumerBrandedHeaderFeatureEnabled } = useFeature(
    'consumer_entity_branded_header'
  );
  const { loading: isMoveUPOLoading, data: { isUPO } = {} } = useIsMoveUPO();
  const { on: showUPOCountdown } = useFeature('show_upo_countdown');
  const { on: showProgressBar } = useFeature(
    'consumer_app_use_essentials_progress_bar'
  );
  const shouldShowUPOCountdown = showUPOCountdown && isUPO;
  const router = useRouter();
  const { nextItem } = useDaisyChain() || {};

  const handleStartTask = () => {
    if (nextItem?.route) {
      router.push(nextItem.route);
    } else {
      router.push('/');
    }
  };

  const user = useUserData();

  const hasRecommendedCards = recommendedCards && recommendedCards.length > 0;
  const hasPriorityTodoCards =
    priorityCards && priorityCards.length > 0 && isUPO;

  const { data: itemDefinitionsData } = useSWR<ItemDefinition[]>(
    '/v2/item_definitions'
  );

  const { trackEvent } = useTracking<TrackEventInput<unknown>>();

  const showPropertyContact = useIsPropertyValid();

  const updaterPro = () => {
    let isBetween;
    let updaterLite;
    if (user.user?.createdAt) {
      const createdAtDate = new Date(user.user.createdAt);
      const today = new Date(Date.now());
      const firstDate = addDays(createdAtDate, 1);
      const secondDate = addDays(createdAtDate, 2);
      isBetween = !!(today <= secondDate && today >= firstDate);
    }

    if (itemDefinitionsData) {
      updaterLite =
        itemDefinitionsData.filter(({ entity }) => {
          return entity?.code === 'd2c' || entity?.id === 2;
        }).length > 0;
    }

    const shouldShow = isBetween && updaterLite;

    if (shouldShow) {
      trackEvent({
        verb: 'displayed',
        object: 'updater_pro_notice',
        details: {
          language: {
            ...UpdaterProLanguage,
          },
        },
      });
    }

    return shouldShow;
  };

  // Loading state
  if (loading || loadingTitle || isMoveUPOLoading || cardsLoading) {
    return (
      <Box width="100%">
        <SpinnerFullPage data-testid="essential-layout-loader" />
      </Box>
    );
  }

  return (
    <Flex flexDirection="column" width="100%" className="essential-layout">
      <BrandingProBar />
      {/* @ts-expect-error children issue */}
      <NativeWebElement as={null}>
        <AppBanner propertyName={propertyName ? propertyName : undefined} />
      </NativeWebElement>

      {isWhiteLabeled ? (
        <WhiteLabelHero
          firstName={user.user?.firstName}
          totalTasks={priorityCards.length + completedRequiredCards.length}
          completedTasks={completedRequiredCards.length}
          validatingTasks={validatingCards.length}
        />
      ) : (
        // @ts-expect-error FIXME: TS2322 - strict mode migration
        <HeroSection height={['100%']} ref={heroRef} as="section">
          <HeroInner>
            {/* Content that fades on scroll */}
            <HeroContent contentOpacity={opacity}>
              {/* Brand logo */}
              {brandLogo &&
                isHomeowner !== 'loading' &&
                !isHomeowner &&
                !consumerBrandedHeaderFeatureEnabled && (
                  <Box height={['32px']} width="124px" mb="m">
                    <PartnerImage
                      maxHeight="100%"
                      maxWidth="100%"
                      src={brandLogo}
                      alt="brand-logo"
                    />
                  </Box>
                )}

              {greeting && (
                <Text
                  data-test-id="tab-greeting"
                  variant={['s', 's', 'm']}
                  mb="xs"
                >
                  {greeting}
                </Text>
              )}
              <Text
                variant={['xlBold', 'xlBold', 'xxlBold', 'xxlBold', 'xxlBold']}
                mb="m"
                as="h1"
              >
                {title}
              </Text>

              {showPropertyContact ? <PropertyContactPageLink /> : null}

              {showProgressBar && isUPO && (
                <DesktopProgressBarContainer>
                  <ProgressBarCard
                    priorityCards={priorityCards as InitiatePayload[]}
                    completedCards={completedCards as InitiatePayload[]}
                    validatingCards={validatingCards as InitiatePayload[]}
                    currentMoveDate={user.user?.currentMove?.date}
                    onStartTask={handleStartTask}
                  />
                </DesktopProgressBarContainer>
              )}
            </HeroContent>

            {showProgressBar && isUPO && (
              <MobileProgressBarContainer style={{ opacity }}>
                <ProgressBarCard
                  priorityCards={priorityCards as InitiatePayload[]}
                  completedCards={completedCards as InitiatePayload[]}
                  validatingCards={validatingCards as InitiatePayload[]}
                  currentMoveDate={user.user?.currentMove?.date}
                  onStartTask={handleStartTask}
                />
              </MobileProgressBarContainer>
            )}

            {/* Mobile Illustration */}
            <IllustrationMobile>
              <IllustrationMoverMobile mobileTranslateY={mobileTranslateY}>
                <Image
                  src="/images/illustrations/animated-moving-illustration.svg"
                  alt=""
                  width="100%"
                />
              </IllustrationMoverMobile>
            </IllustrationMobile>

            {/* Desktop Illustration */}
            <IllustrationDesktop>
              <IllustrationMoverDesktop desktopTranslateY={translateY}>
                <Image
                  src="/images/illustrations/animated-moving-illustration.svg"
                  alt=""
                  width="100%"
                />
              </IllustrationMoverDesktop>
            </IllustrationDesktop>
          </HeroInner>
        </HeroSection>
      )}

      <WhiteLabelConstraint>
        <TabLayoutContainer marginTop="m">
          {hasPriorityTodoCards && (
            <>
              <TabLayoutCardLayout
                gridTemplateColumns={['1fr', '1fr', 'repeat(2, 1fr)']}
                gridGap={['s', 's', 'm', 'm']}
                data={priorityCards}
                section="todo"
                title={
                  shouldShowUPOCountdown &&
                  priorityCards.length > 0 && (
                    <UPOPriorityHeader
                      tasksLeftToGo={priorityCards.length}
                      showCount={showProgressBar}
                    />
                  )
                }
              />
            </>
          )}

          {hasRecommendedCards && (
            <>
              <Box marginTop="xxxl" />
              <TabLayoutCardLayout
                title={
                  hasRecommendedCards && isUPO ? (
                    <>
                      Recommended tasks
                      {priorityCards?.length === 0 && (
                        <Text variant="m">
                          Great job - you&apos;ve completed the necessities!
                        </Text>
                      )}
                    </>
                  ) : undefined
                }
                gridTemplateColumns={['1fr', '1fr', 'repeat(2, 1fr)']}
                gridGap={['s', 's', 'm', 'm']}
                data={recommendedCards}
                section="todo"
              />
            </>
          )}

          {hasCompletedCards && (
            <>
              <Box marginTop="xxxl" />
              <TabLayoutCardLayout
                title={
                  hasPriorityTodoCards || hasRecommendedCards
                    ? 'Completed tasks'
                    : undefined
                }
                gridTemplateColumns={['1fr', '1fr', 'repeat(2, 1fr)']}
                gridGap={['s', 's', 'm', 'm']}
                data={completedCards}
                section="done"
              />
            </>
          )}

          {updaterPro() && (
            <>
              <Box
                width="100%"
                marginTop="xxxl"
                borderTop="2px dashed #B3B3B3"
                paddingTop="l"
              />

              <Text variant="sBold" color={`${Palette.gray50}`}>
                {UpdaterProLanguage.title}
              </Text>
              <Text marginTop="xs" variant="xs" color={`${Palette.gray700}`}>
                {UpdaterProLanguage.body}{' '}
              </Text>
            </>
          )}
        </TabLayoutContainer>
      </WhiteLabelConstraint>
    </Flex>
  );
};

export type UPOPriorityHeaderProps = {
  tasksLeftToGo: number;
  showCount: boolean;
};

export const UPOPriorityHeader: React.FC<UPOPriorityHeaderProps> = ({
  tasksLeftToGo,
  showCount,
}) => {
  return (
    <>
      Priority tasks{' '}
      {showCount && (
        <Text variant="m" as="span">
          {tasksLeftToGo} left to go!
        </Text>
      )}
    </>
  );
};

export { EssentialLayout };
